/**
 * Image frames, Canva-style: an image sits in a frame with a fixed shape, and
 * replacing the photo keeps the frame — the new photo fills it, cropped to fit.
 */

export type FrameRatio = "original" | "1:1" | "4:5" | "3:4" | "4:3" | "3:2" | "16:9" | "21:9";
export type FrameFit = "cover" | "contain";

export const FRAME_RATIOS: { id: FrameRatio; label: string; value: number | null }[] = [
  { id: "original", label: "Original", value: null },
  { id: "1:1", label: "Square", value: 1 },
  { id: "4:5", label: "Portrait", value: 4 / 5 },
  { id: "3:4", label: "Tall", value: 3 / 4 },
  { id: "4:3", label: "Landscape", value: 4 / 3 },
  { id: "3:2", label: "Photo", value: 3 / 2 },
  { id: "16:9", label: "Wide", value: 16 / 9 },
  { id: "21:9", label: "Banner", value: 21 / 9 },
];

export const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
export const IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];

/** The preset closest in shape to a width/height ratio. */
export function nearestFrameRatio(ratio: number): Exclude<FrameRatio, "original"> {
  let best: Exclude<FrameRatio, "original"> = "1:1";
  let bestDistance = Infinity;
  for (const option of FRAME_RATIOS) {
    if (option.value === null) continue;
    const distance = Math.abs(Math.log(ratio / option.value));
    if (distance < bestDistance) {
      bestDistance = distance;
      best = option.id as Exclude<FrameRatio, "original">;
    }
  }
  return best;
}

/**
 * Props for putting a new photo into an image's frame. A frame without a fixed
 * shape takes the shape the old photo had on the page, so the layout stays put.
 */
export function replaceImageProps(
  props: Record<string, unknown>,
  src: string,
  renderedRatio: number | null,
): Record<string, unknown> {
  const current = typeof props.aspectRatio === "string" ? (props.aspectRatio as FrameRatio) : "original";
  const hadPhoto = typeof props.src === "string" && props.src.length > 0;
  const aspectRatio =
    current !== "original" || !hadPhoto || !renderedRatio || !Number.isFinite(renderedRatio)
      ? current
      : nearestFrameRatio(renderedRatio);
  return {
    src,
    assetId: null,
    aspectRatio,
    fit: props.fit === "contain" ? "contain" : "cover",
    focalX: 50,
    focalY: 50,
    zoom: 1,
  };
}

/** A user-facing problem with a chosen file, or null when it can be used. */
export function imageFileProblem(file: File): string | null {
  if (!IMAGE_TYPES.includes(file.type)) return "Images must be JPEG, PNG, WebP or GIF.";
  if (file.size > MAX_IMAGE_BYTES) return "Images must be 5 MB or smaller.";
  return null;
}

/** Reads a file as a data URL — the fallback when no uploader is configured. */
export function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error("Could not read the image."));
    reader.readAsDataURL(file);
  });
}

export function clampPercent(value: number): number {
  return Math.round(Math.min(100, Math.max(0, value)));
}
